import { Box, Typography } from "@mui/material";
import React, { useMemo } from "react";
import { brand } from "../../../shared/theme/app-theme";
import { ReportStatus } from "../../report/models";
import { DashboardDTO } from "../model";
import PeriodStatusChart from "./period-status-chart";

type Props = {
  data?: DashboardDTO;
};

const ATTENTION: ReportStatus[] = [
  ReportStatus.LOADED,
  ReportStatus.PENDING,
  ReportStatus.FOR_REVISION,
  ReportStatus.ERROR,
];

const IN_PROGRESS: ReportStatus[] = [
  ReportStatus.ACCEPTED,
  ReportStatus.SCHOLARSHIP_CALCULATION,
  ReportStatus.APPROVED_BY_UNIVERSITY,
  ReportStatus.MT102,
  ReportStatus.MT102_CREATED,
];

const DONE: ReportStatus[] = [
  ReportStatus.PAID_IN_PART,
  ReportStatus.PAID,
];

function sumByStatuses(
  data: DashboardDTO | undefined,
  statuses: ReportStatus[]
) {
  const wanted = new Set(statuses);
  return (data?.dashboard ?? []).reduce((sum, item) => {
    if (item.status === "TOTAL" || !wanted.has(item.status)) return sum;
    return sum + (item.data?.total ?? 0);
  }, 0);
}

const PeriodStatusTable: React.FC<Props> = ({ data }) => {
  const summary = useMemo(() => {
    const total = data?.total ?? 0;
    const attention = sumByStatuses(data, ATTENTION);
    const inProgress = sumByStatuses(data, IN_PROGRESS);
    const done = sumByStatuses(data, DONE);
    return [
      {
        key: "attention",
        label: "На контроле",
        hint: "новые, на рассмотрении, доработка, ошибка",
        value: attention,
        color: "#b42318",
        total,
      },
      {
        key: "progress",
        label: "В процессе",
        hint: "принят, расчёт, МТ102",
        value: inProgress,
        color: brand.govBlue,
        total,
      },
      {
        key: "done",
        label: "Выплачено",
        hint: "частично и полностью",
        value: done,
        color: "#1f7a4d",
        total,
      },
    ];
  }, [data]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PeriodStatusChart data={data} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 1.5,
        }}
      >
        {summary.map((item) => {
          const pct = item.total > 0 ? Math.round((item.value / item.total) * 100) : 0;
          return (
            <Box
              key={item.key}
              sx={{
                px: 2,
                py: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                {item.label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.25 }}>
                <Typography
                  sx={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, color: item.color }}
                >
                  {item.value}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {pct}%
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.5 }}>
                {item.hint}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default PeriodStatusTable;
